import { defineMessages } from 'react-intl';

/**
 * Every string of the artist Home (design: music24-artist-home a-e).
 *
 * The native apps read these ids too (`scripts/music24-native/i18n-keys.txt`), so keep the ids
 * stable. The period is 28 days on paid plans and on the trial, and 7 days on Free.
 */
export const artistHomeMessages = defineMessages({
  pageTitle: { id: 'app.sml.artistHome.title', defaultMessage: 'Home' },
  period: { id: 'app.sml.artistHome.period', defaultMessage: 'Last {days} days · {range}' },
  // The old "28 days on {plan}" (`app.sml.artistHome.period.lock`) read unclear next to "You are on
  // Free" (onboarding review 2026-10-19). The native apps still render the old id (nativeMessages.ts).
  periodLockDays: {
    id: 'app.sml.artistHome.period.lockDays',
    defaultMessage: '{currentPlan} shows {shortDays} days. {plan} shows {fullDays}.',
  },
  profilesRead: {
    id: 'app.sml.artistHome.profilesRead',
    defaultMessage: '{count, plural, one {# profile read daily} other {# profiles read daily}}',
  },

  sectionAudience: { id: 'app.sml.artistHome.section.audience', defaultMessage: 'Audience' },
  sectionStreams: { id: 'app.sml.artistHome.section.streams', defaultMessage: 'Streams and smartlinks' },

  kpiFollowers: { id: 'app.sml.artistHome.kpi.followers', defaultMessage: '{service} followers' },
  kpiSubscribers: { id: 'app.sml.artistHome.kpi.subscribers', defaultMessage: '{service} subscribers' },
  kpiFans: { id: 'app.sml.artistHome.kpi.fans', defaultMessage: '{service} fans' },
  kpiMonthlyListeners: { id: 'app.sml.artistHome.kpi.monthlyListeners', defaultMessage: 'Monthly listeners' },
  kpiStreams: { id: 'app.sml.artistHome.kpi.streams', defaultMessage: 'Streams' },
  kpiListeners: { id: 'app.sml.artistHome.kpi.listeners', defaultMessage: 'Listeners' },
  kpiSmartlinkViews: { id: 'app.sml.artistHome.kpi.smartlinkViews', defaultMessage: 'Smartlink views' },
  kpiSmartlinkClicks: { id: 'app.sml.artistHome.kpi.smartlinkClicks', defaultMessage: 'Smartlink clicks' },
  kpiShazams: { id: 'app.sml.artistHome.streams.shazams', defaultMessage: 'Shazams' },
  kpiRadioSpins: { id: 'app.sml.artistHome.streams.radioSpins', defaultMessage: 'Radio spins' },
  kpiFirstWeek: { id: 'app.sml.artistHome.kpi.firstWeek', defaultMessage: 'First week' },
  kpiNotRead: { id: 'app.sml.artistHome.kpi.notRead', defaultMessage: 'Not read' },
  sourceProfile: { id: 'app.sml.artistHome.source.profile', defaultMessage: 'Public profile, read daily' },
  sourceSpotifyForArtists: { id: 'app.sml.artistHome.source.spotifyForArtists', defaultMessage: 'Spotify for Artists' },
  sourcePortals: { id: 'app.sml.artistHome.source.portals', defaultMessage: 'Artist portals' },
  sourceLivePages: {
    id: 'app.sml.artistHome.source.livePages',
    defaultMessage: '{count, plural, one {# live page} other {# live pages}}',
  },
  sourceServices: {
    id: 'app.sml.artistHome.source.services',
    defaultMessage: '{count, plural, one {To # service} other {To # services}}',
  },

  moreServicesTitle: { id: 'app.sml.artistHome.moreServices.title', defaultMessage: 'More services' },
  moreServicesBody: {
    id: 'app.sml.artistHome.moreServices.body',
    defaultMessage: "Link the artist's profile on another service. MUSIC24 then reads its followers every day.",
  },
  moreServicesCta: { id: 'app.sml.artistHome.moreServices.cta', defaultMessage: 'Link a profile' },
  portalsSetupTitle: {
    id: 'app.sml.artistHome.portalsSetup.title',
    defaultMessage: 'See your real streams and listeners',
  },
  portalsSetupBody: {
    id: 'app.sml.artistHome.portalsSetup.body',
    defaultMessage:
      'Connect Spotify for Artists. Home then shows streams and listeners for this period. YouTube and Deezer for Creators work too.',
  },
  portalsSetupCta: { id: 'app.sml.artistHome.portalsSetup.cta', defaultMessage: 'Connect Spotify for Artists' },

  catalogTitle: { id: 'app.sml.artistHome.catalog.title', defaultMessage: 'Catalog' },
  catalogOpen: { id: 'app.sml.artistHome.catalog.open', defaultMessage: 'Open Catalog' },
  catalogLatest: { id: 'app.sml.artistHome.catalog.latest', defaultMessage: 'Latest release' },
  catalogReleased: { id: 'app.sml.artistHome.catalog.released', defaultMessage: '{type} · out {date}' },
  catalogReleases: { id: 'app.sml.artistHome.catalog.releases', defaultMessage: 'Releases' },
  catalogMissingLinks: { id: 'app.sml.artistHome.catalog.missingLinks', defaultMessage: 'Missing links' },
  catalogNoSmartlink: { id: 'app.sml.artistHome.catalog.noSmartlink', defaultMessage: 'No smartlink' },
  catalogSmartlinkLive: { id: 'app.sml.artistHome.catalog.smartlinkLive', defaultMessage: 'Smartlink live' },
  catalogSmartlinkDraft: { id: 'app.sml.artistHome.catalog.smartlinkDraft', defaultMessage: 'Smartlink draft' },
  catalogCreateSmartlink: { id: 'app.sml.artistHome.catalog.createSmartlink', defaultMessage: 'Create smartlink' },
  catalogFixLinks: { id: 'app.sml.artistHome.catalog.fixLinks', defaultMessage: 'Fix links' },
  catalogMissingServices: { id: 'app.sml.artistHome.catalog.missingServices', defaultMessage: 'No {services} link' },
  catalogNoSmartlinkYet: { id: 'app.sml.artistHome.catalog.noSmartlinkYet', defaultMessage: 'No smartlink yet' },
  catalogEmptyTitle: { id: 'app.sml.artistHome.catalog.empty.title', defaultMessage: 'No release in your catalog yet' },
  catalogEmptyBody: {
    id: 'app.sml.artistHome.catalog.empty.body',
    defaultMessage:
      'Claim the releases that are yours. MUSIC24 finds their links on every service and tells you when one is missing.',
  },
  catalogEmptyCta: { id: 'app.sml.artistHome.catalog.empty.cta', defaultMessage: 'Claim your first release' },

  smartlinksTitle: { id: 'app.sml.artistHome.smartlinks.title', defaultMessage: 'Smartlinks performance' },
  smartlinksOpen: { id: 'app.sml.artistHome.smartlinks.open', defaultMessage: 'Open Smartlinks' },
  smartlinksCtr: { id: 'app.sml.artistHome.smartlinks.ctr', defaultMessage: 'Click-through' },
  smartlinksVisitors: { id: 'app.sml.artistHome.smartlinks.visitors', defaultMessage: 'Visitors' },
  smartlinksPresaves: { id: 'app.sml.artistHome.smartlinks.presaves', defaultMessage: 'Pre-saves' },
  smartlinksPerDay: { id: 'app.sml.artistHome.smartlinks.perDay', defaultMessage: 'Views and clicks per day' },
  smartlinksTopPages: { id: 'app.sml.artistHome.smartlinks.topPages', defaultMessage: 'Top pages · clicks' },
  smartlinksServices: { id: 'app.sml.artistHome.smartlinks.services', defaultMessage: 'Services · clicks' },
  smartlinksCountries: { id: 'app.sml.artistHome.smartlinks.countries', defaultMessage: 'Countries · views' },
  smartlinksTabPages: { id: 'app.sml.artistHome.smartlinks.tab.pages', defaultMessage: 'Pages' },
  smartlinksTabServices: { id: 'app.sml.artistHome.smartlinks.tab.services', defaultMessage: 'Services' },
  smartlinksTabCountries: { id: 'app.sml.artistHome.smartlinks.tab.countries', defaultMessage: 'Countries' },
  smartlinksPointChange: { id: 'app.sml.artistHome.smartlinks.pointChange', defaultMessage: '{value} pt' },
  smartlinksEmptyTitle: { id: 'app.sml.artistHome.smartlinks.empty.title', defaultMessage: 'No smartlinks yet' },
  smartlinksEmptyBody: {
    id: 'app.sml.artistHome.smartlinks.empty.body',
    defaultMessage: 'Create a smartlink for a release and watch views, clicks, services and countries roll in.',
  },
  smartlinksEmptyCta: { id: 'app.sml.artistHome.smartlinks.empty.cta', defaultMessage: 'Create your first smartlink' },

  promoteTitle: { id: 'app.sml.artistHome.promote.title', defaultMessage: 'Promote' },
  promoteOpen: { id: 'app.sml.artistHome.promote.open', defaultMessage: 'Open Calendar' },
  promoteNextScheduled: { id: 'app.sml.artistHome.promote.nextScheduled', defaultMessage: 'Next scheduled' },
  promoteNothingScheduled: { id: 'app.sml.artistHome.promote.nothingScheduled', defaultMessage: 'Nothing scheduled' },
  promoteNeedsYou: { id: 'app.sml.artistHome.promote.needsYou', defaultMessage: 'Needs you' },
  promoteFailedPosts: {
    id: 'app.sml.artistHome.promote.failedPosts',
    defaultMessage: '{count, plural, one {# post failed to publish} other {# posts failed to publish}}',
  },
  promoteReview: { id: 'app.sml.artistHome.promote.review', defaultMessage: 'Review' },
  promoteReconnectTitle: {
    id: 'app.sml.artistHome.promote.reconnect.title',
    defaultMessage: '{channel} needs a new sign-in',
  },
  promoteReconnectBody: {
    id: 'app.sml.artistHome.promote.reconnect.body',
    defaultMessage: 'Posts to it wait until you reconnect.',
  },
  promoteReconnect: { id: 'app.sml.artistHome.promote.reconnect', defaultMessage: 'Reconnect' },
  promoteLatestReels: { id: 'app.sml.artistHome.promote.latestReels', defaultMessage: 'Latest reels' },
  promoteReelStudio: { id: 'app.sml.artistHome.promote.reelStudio', defaultMessage: 'Studio' },
  promoteToday: { id: 'app.sml.artistHome.promote.today', defaultMessage: 'Today' },
  promoteEmptyTitle: {
    id: 'app.sml.artistHome.promote.empty.title',
    defaultMessage: 'Post to your socials from MUSIC24',
  },
  promoteEmptyBody: {
    id: 'app.sml.artistHome.promote.empty.body',
    defaultMessage:
      'Connect Instagram, TikTok, YouTube or another channel. Home then shows your next posts, failed posts and new reels.',
  },
  promoteEmptyCta: { id: 'app.sml.artistHome.promote.empty.cta', defaultMessage: 'Connect a channel' },

  noArtistTitle: { id: 'app.sml.artistHome.noArtist.title', defaultMessage: 'Add an artist to fill Home' },
  noArtistBody: {
    id: 'app.sml.artistHome.noArtist.body',
    defaultMessage:
      'Home shows one artist at a time: audience, streams, releases, smartlinks and posts for the last 28 days. Add the artist you work on to start.',
  },
  noArtistCta: { id: 'app.sml.artistHome.noArtist.cta', defaultMessage: 'Add an artist' },

  blockError: {
    id: 'app.sml.artistHome.blockError',
    defaultMessage: 'We could not load this block. The rest of Home still works.',
  },
  tryAgain: { id: 'app.sml.artistHome.tryAgain', defaultMessage: 'Try again' },
});
